import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  TIME_SLOTS_24H,
  MOCK_SPECIES_ACTIVITY_TREND,
  DARK_THEME_EXPORTING,
  DARK_THEME_NAVIGATION,
} from '../bioacousticMockData';

interface SpeciesActivityTrendChartProps {
  seriesData?: { name: string; color: string; data: number[] }[];
  height?: number;
  title?: string;
}

const PAGE_SIZE = 10;

const SpeciesActivityTrendChart: React.FC<SpeciesActivityTrendChartProps> = ({
  seriesData = MOCK_SPECIES_ACTIVITY_TREND,
  height = 340,
  title = 'Species Activity Trend',
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedSpeciesNames, setSelectedSpeciesNames] = useState<string[]>([]);

  const totalPages = Math.ceil(seriesData.length / PAGE_SIZE);

  // Active displayed series based on dropdown selection or pagination
  const activeSeries = useMemo(() => {
    if (selectedSpeciesNames.length > 0) {
      return seriesData.filter((s) => selectedSpeciesNames.includes(s.name));
    }
    const start = currentPage * PAGE_SIZE;
    return seriesData.slice(start, start + PAGE_SIZE);
  }, [seriesData, currentPage, selectedSpeciesNames]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const chartOptions: Highcharts.Options = useMemo(() => {
    return {
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height,
        style: {
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      },
      exporting: DARK_THEME_EXPORTING,
      navigation: DARK_THEME_NAVIGATION,
      title: {
        text: '',
      },
      credits: { enabled: false },
      xAxis: {
        categories: TIME_SLOTS_24H,
        title: {
          text: 'Time of Day (24-Hour Cycle)',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          rotation: -45,
          style: { color: '#888888', fontSize: '10px' },
        },
        lineColor: '#222222',
        tickColor: '#222222',
        gridLineColor: 'rgba(255, 255, 255, 0.03)',
        crosshair: {
          color: 'rgba(16, 185, 129, 0.3)',
          dashStyle: 'Dash' as const,
        },
      },
      yAxis: {
        title: {
          text: 'Acoustic Activity Detections',
          style: { color: '#777777', fontSize: '11px', fontWeight: '600' },
        },
        labels: {
          style: { color: '#888888', fontSize: '10px' },
        },
        min: 0,
        max: 300,
        tickInterval: 100,
        gridLineColor: '#1A1A1A',
      },
      tooltip: {
        backgroundColor: '#111111',
        borderColor: '#222222',
        style: { color: '#EEEEEE', fontSize: '11px' },
        shared: true,
        valueSuffix: ' calls',
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: {
            enabled: true,
            radius: 3,
            symbol: 'circle',
          },
        },
      },
      legend: {
        enabled: false,
      },
      series: activeSeries.map((s) => ({
        type: 'spline',
        name: s.name,
        color: s.color,
        data: s.data,
        marker: {
          fillColor: s.color,
        },
      })),
    };
  }, [activeSeries, height]);

  return (
    <Box
      sx={{
        background: 'rgba(10, 10, 10, 0.85)',
        border: '1px solid #1E1E1E',
        borderRadius: '12px',
        p: 2.5,
      }}
    >
      {/* Top Header & Pagination Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid #1C1C1C',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#666666', mt: 0.25 }}>
            Diurnal activity curves across 24h cycle
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Species Multi-Select Dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FilterIcon sx={{ fontSize: 14, color: '#10B981' }} />
            <Select
              multiple
              size="small"
              value={selectedSpeciesNames}
              onChange={(e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]);
                setSelectedSpeciesNames(val);
              }}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <span style={{ color: '#888', fontSize: '11px' }}>Showing Page (10 Species)</span>;
                }
                return (
                  <span style={{ color: '#10B981', fontSize: '11px', fontWeight: 700 }}>
                    {selected.length} Selected
                  </span>
                );
              }}
              sx={{
                bgcolor: '#111',
                color: '#fff',
                fontSize: '11px',
                height: '32px',
                borderRadius: '6px',
                border: '1px solid #282828',
                minWidth: '190px',
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiSvgIcon-root': { color: '#10B981', fontSize: 18 },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    maxHeight: 280,
                    '& .MuiMenuItem-root': {
                      fontSize: '11px',
                      py: 0.5,
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              {seriesData.map((s) => (
                <MenuItem key={s.name} value={s.name}>
                  <Checkbox
                    checked={selectedSpeciesNames.indexOf(s.name) > -1}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: '#444',
                      '&.Mui-checked': { color: '#10B981' },
                    }}
                  />
                  <ListItemText
                    primary={s.name}
                    primaryTypographyProps={{ fontSize: '11px', color: '#ccc' }}
                  />
                </MenuItem>
              ))}
            </Select>

            {selectedSpeciesNames.length > 0 && (
              <Chip
                label="Reset"
                size="small"
                onClick={() => setSelectedSpeciesNames([])}
                sx={{
                  bgcolor: 'rgba(239,68,68,0.1)',
                  color: '#EF4444',
                  border: '1px solid rgba(239,68,68,0.25)',
                  fontSize: '10px',
                  fontWeight: 700,
                  height: '24px',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(239,68,68,0.2)' },
                }}
              />
            )}
          </Box>

          {/* Pagination Controls (Only when not in custom multi-select mode) */}
          {selectedSpeciesNames.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid #252525',
                borderRadius: '6px',
                px: 1,
                py: 0.25,
              }}
            >
              <Button
                size="small"
                disabled={currentPage === 0}
                onClick={handlePrevPage}
                sx={{
                  minWidth: '24px',
                  p: 0.25,
                  color: '#10B981',
                  '&.Mui-disabled': { color: '#444' },
                }}
              >
                <PrevIcon sx={{ fontSize: 16 }} />
              </Button>

              <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 700, px: 0.5 }}>
                Page {currentPage + 1} of {totalPages} (Species {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, seriesData.length)})
              </Typography>

              <Button
                size="small"
                disabled={currentPage >= totalPages - 1}
                onClick={handleNextPage}
                sx={{
                  minWidth: '24px',
                  p: 0.25,
                  color: '#10B981',
                  '&.Mui-disabled': { color: '#444' },
                }}
              >
                <NextIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Chart Canvas */}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />

      {/* Sleek Compact Species Legend Grid (Replaces clumsy up/down buttons, fixed compact height) */}
      <Box
        sx={{
          mt: 2,
          pt: 1.5,
          borderTop: '1px solid #1A1A1A',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center',
          maxHeight: '68px',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: '#252525', borderRadius: '2px' },
        }}
      >
        {activeSeries.map((s) => (
          <Box
            key={s.name}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: `1px solid ${s.color}33`,
              borderRadius: '6px',
              px: 1,
              py: 0.35,
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.06)',
                borderColor: s.color,
              },
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: s.color,
                boxShadow: `0 0 6px ${s.color}`,
                flexShrink: 0,
              }}
            />
            <Typography
              noWrap
              sx={{
                fontSize: '10px',
                color: '#DDDDDD',
                fontWeight: 600,
                maxWidth: '220px',
              }}
            >
              {s.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SpeciesActivityTrendChart;

